/**
 * SearchOptions
 * - page: page number
 * - hitsPerPage: number of items per page
 * - getCategoryData: if true, also return list of all the unique categories and number of hits per category. Bypasses pagination;
 * - facetFilters: array of FacetFilters
 * @note currently facet filters are ANDed together
 * @note if OR behaviour is required, then contact iw to discuss
 */
/**
 * Valid sort directions for Algolia searches
 * @example 'warranty_startDate:desc' -> direction = 'desc'
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Convert sort direction to multiplier for sorting logic
 * @param direction - 'asc' | 'desc'
 * @returns -1 for desc, 1 for asc
 */
export const getDirectionMultiplier = (direction: SortDirection): number =>
  direction === 'desc' ? -1 : 1;

/**
 * Numeric filter operators for Algolia searches
 */
export type NumericOperator = '>=' | '<=' | '>' | '<' | '=' | '!=';

/**
 * Structured numeric filter object for type safety and validation
 */
export interface NumericFilter {
  name: string;
  operator: NumericOperator;
  value: number;
}

export interface SearchOptions {
  page?: number;
  hitsPerPage?: number;
  //getCategoryData?: boolean;
  facetFilters?: FacetFilter[];
  restrictToAttribues?: string[];
  numericFilters?: NumericFilter[]; // Structured objects only: [{name, operator, value}]
  sort?: string[]; // Array of sort expressions like ['warranty_endDate:desc', 'purchase_price:asc'];
  filters?: string;
}

/**
 * SearchResultItem
 * - objectID: unique ID for the item
 * - groupName: group name for deduplication (not currently used as we're not using a hierarchical index)
 * - depth: depth of the item in the hierarchy (not currently used as we're not using a hierarchical index)
 * - score: search ranking score
 * - payload: the item itself
 */
export interface SearchResultItem<TYPE> {
  objectID: string;
  groupName?: string;
  depth?: number;
  score: number;
  payload: TYPE & { [key: string]: any };
  [key: string]: any;
}

/**
 * SearchResults
 * - numitems: total number of items found
 * - page: current page number
 * - numpages: total number of pages
 * - items: array of items
 */
export interface SearchResults<TYPE> {
  numitems: number;
  page: number;
  numpages: number;
  items: TYPE[];
}

/**
 * FacetItem
 * Single element for a Facet
 * - name: the name of the facet
 * - count: for search ranking, generally the number of times the facet was found
 */
export interface FacetItem {
  name: string;
  count: number;
}

/**
 * FacetFilter
 * - name: name of the facet
 * - value: value of the facet
 * @note currently this is only used for filtering by colour.name and series
 * @note see SearchVendableFacet for more details
 */
export interface FacetFilter {
  name: string;
  value: string | boolean | string[];
}

/**
 * Enhanced SearchResults with rich pagination metadata
 * Perfect for frontend consumption and API responses
 */
export interface EnhancedSearchResults<TYPE> extends SearchResults<TYPE> {
  pagination: {
    totalRecords: number; // Same as numitems, but clearer name
    totalPages: number; // Same as numpages, but clearer name
    currentPage: number; // Same as page, but 1-based for UI
    recordsPerPage: number; // hitsPerPage setting used
    recordsInThisPage: number; // Actual count in current page
    hasNextPage: boolean; // Convenient boolean
    hasPreviousPage: boolean; // Convenient boolean
    nextPage: number | null; // Next page number (1-based) or null
    previousPage: number | null; // Previous page number (1-based) or null
    pageRange: {
      start: number; // Record number where page starts (1-based)
      end: number; // Record number where page ends (1-based)
    };
  };
}

// ===========================
// ALGOLIA SORTING CONFIGURATION
// ===========================

/**
 * Fields extracted to top-level by the transform function
 * These fields don't need the 'payload.' prefix in Algolia queries
 */
export const TOP_LEVEL_FIELDS = [
  'warranty_startDate',
  'warranty_endDate',
  'warranty_registeredTime',
  'warranty_status',
  'warranty_duration',
  'warranty_claim_limit',
  'purchase_time',
  'purchase_price',
  'tcreate',
  'register_time',
] as const;

/**
 * Fields that are actually sortable (subset of TOP_LEVEL_FIELDS)
 * These are timestamp fields that can be used for sorting records
 */
export const SORTABLE_FIELDS = [
  'warranty_startDate',
  'warranty_endDate',
  'warranty_registeredTime',
  'purchase_time',
  'tcreate',
  'register_time',
] as const;

/**
 * @deprecated REPLICA_SUPPORTED_FIELDS is now dynamically discovered
 * Use the index-discovery service instead:
 * - getCachedIndexInfo() to get all available replicas
 * - isFieldSortable() to check if a field is sortable
 * - getReplicaIndexName() to get the correct replica for sorting
 * 
 * This constant is kept for backward compatibility but should not be used.
 */
export const REPLICA_SUPPORTED_FIELDS = [
  // Deprecated: Fields are now dynamically discovered from Algolia
] as const;

// ===========================
// NUMERIC FILTER HELPERS
// ===========================

/**
 * Converts structured numeric filter objects to Algolia's string format
 * @param filters Array of structured filter objects
 * @returns Array of Algolia-compatible filter strings
 *
 * @example
 * normalizeNumericFilters([
 *   { name: 'warranty_startDate', operator: '>=', value: 1704067200000 },
 *   { name: 'warranty_endDate', operator: '<=', value: 1735689599000 }
 * ])
 * // Returns: ["warranty_startDate >= 1704067200000", "warranty_endDate <= 1735689599000"]
 */
export function normalizeNumericFilters(filters: NumericFilter[]): string[] {
  return filters.map((filter) => {
    // Validate the object structure
    if (
      !filter.name ||
      !filter.operator ||
      !Number.isFinite(filter.value) ||
      typeof filter.value !== 'number'
    ) {
      throw new Error(
        `Invalid numeric filter object: ${JSON.stringify(filter)}`
      );
    }
    return `${filter.name} ${filter.operator} ${filter.value}`;
  });
}
